import { BadgeRussianRuble, Clipboard } from "lucide-react"
import React, { useEffect, useRef } from "react"
import { PaymentStatus, paymentStatusMapping } from "../../models/payment"
import AppError from "../../shared/components/Error"
import FullScreenLoader from "../../shared/components/FullScreenLoader"
import { useGetPayHistory } from "../../shared/hooks/useGetPayHistory"
import { notify } from "../../shared/utils/notification"
import { copyToClipboard } from "../../utils/clipboard"
import './PayHistoryScreen.css'

function formatDate(date: string): string {
    return new Date(date).toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatStatus(status: PaymentStatus): string {
    return paymentStatusMapping[status] || '';
}

export const shortString = (value: string, head = 4, tail = 4): string => {
    if (!value) return '';
    if (value.length <= head + tail + 1) return value;
    return `${value.slice(0, head)}…${value.slice(-tail)}`;
};

export interface PHScreenProps {
    delay?: number;
    optClass?: string;
}

export const PayHistoryScreen: React.FC<PHScreenProps> = ({delay = 0, optClass}) => {
    const {
        payHistory,
        isLoading,
        error,
        loadNextPage,
        hasMore
    } = useGetPayHistory();

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            const el = containerRef.current;
            if (!el || isLoading || !hasMore) return;

            const threshold = 100; // px до низа
            if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
                loadNextPage();
            }
        };

        const el = containerRef.current;
        el?.addEventListener("scroll", handleScroll);
        return () => el?.removeEventListener("scroll", handleScroll);
    }, [loadNextPage, isLoading, hasMore]);

    if (!payHistory.length && isLoading)
        return <FullScreenLoader />;

    return (
        <>
            {error && <AppError />}
            {!error &&
                <div className={`${optClass ?? ''} pay-history-screen`}
                    style={{ animationDelay: `${delay}ms` }}
                    ref={containerRef}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <BadgeRussianRuble/>
                            <h2 className="pay-history-title"> История платежей</h2>
                        </div>

                    {payHistory.length === 0 ? (
                        <div className="pay-history-empty">Нет платежей</div>
                    ) : (
                        <ul className="pay-history-list">
                            {payHistory.map((entry) => (
                                <li className="pay-history-item glass-ui" key={entry.internalPaymentId}>
                                    <div className="ph-amount">
                                        {entry.amount} ₽
                                        <span className={`ph-status ${entry.status.toLowerCase()}`}>
                                            {formatStatus(entry.status)}
                                        </span>
                                    </div>
                                    <div className="ph-meta">
                                        <span>Создано: {formatDate(entry.createdAt)}</span>
                                        <button
                                            type="button"
                                            className="copy-btn"
                                            onClick={() => {
                                                copyToClipboard(entry.internalPaymentId).then(ok => {
                                                    if (ok) notify.info('ID платежа скопирован в буфер обмена');
                                                });
                                            }}>
                                            <div>
                                                Код: {shortString(entry.internalPaymentId)}
                                            </div>
                                            <Clipboard size={14} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                            {isLoading && <div className="ph-loader">Загрузка...</div>}
                            {!hasMore && <div className="ph-end">Это всё 🎉</div>}
                        </ul>
                    )}
                </div >
            }
        </>
    )
}
